import { useMutation } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { useState } from 'react';
import { useGetIdentity, useLogout, useTranslate } from 'ra-core';
import { Avatar, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useUserOrganizations } from '../hooks/useUserOrganizations';

/** Based on the kit `user-menu.tsx`: shows the user's name next to the avatar and adds the organization switcher. */
export const UserMenu = () => {
  const { data: identity } = useGetIdentity();
  const logout = useLogout();
  const translate = useTranslate();
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        render={
          <Button variant="ghost" className="ml-2 h-9 max-w-xs gap-2 px-2" />
        }
      >
        <Avatar className="size-7">
          <AvatarImage src={identity?.avatar} role="presentation" />
        </Avatar>
        <span className="hidden truncate text-sm font-medium sm:inline">
          {identity?.fullName}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" align="end">
        <OrganizationSwitcher />
        <DropdownMenuItem
          onClick={() => void logout()}
          className="cursor-pointer"
        >
          <LogOut />
          {translate('ra.auth.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const OrganizationSwitcher = () => {
  const translate = useTranslate();
  const { organizations, activeOrganization, setActiveOrganization } =
    useUserOrganizations();

  const switchOrganization = useMutation({
    mutationFn: async (organizationId: string) => {
      await setActiveOrganization!({ organization: organizationId });

      // Internally, react-admin uses react-query and caches a bunch of stuff.
      // It also calls the async `getIdentity` function in the authProvider before React has flushed changes, which causes race conditions.
      // A page refresh is the easiest way to invalidate everything and work around these issues, though someone smarter than me can hopefully figure out a better way in the future.
      window.location.reload();
    },
  });

  if (organizations.length <= 1) {
    // Cannot switch organizations if we're not in multiple organizations
    return null;
  }

  return (
    <>
      <DropdownMenuGroup>
        <DropdownMenuLabel>
          {translate('moonstar.menu.company')}
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={activeOrganization?.organization.id}
          onValueChange={(organizationId: string) => {
            if (organizationId !== activeOrganization?.organization.id) {
              switchOrganization.mutate(organizationId);
            }
          }}
          disabled={switchOrganization.isPending}
        >
          {organizations.map(({ organization }) => (
            <DropdownMenuRadioItem
              key={organization.id}
              value={organization.id}
              closeOnClick={false}
            >
              <Avatar className="size-6">
                <AvatarImage
                  src={organization.imageUrl}
                  alt={`${organization.name} logo`}
                />
              </Avatar>
              <span className="truncate">{organization.name}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
    </>
  );
};
